import { createFileRoute } from "@tanstack/react-router";
import { t } from "@lingui/core/macro";
import { useAppConfig } from "@/dataProviders/ConfigDataProvider.tsx";
import { useEffect, useMemo, useState } from "react";
import {
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { useUsersQuery } from "@/modules/users/api/useUsers.ts";
import { keepPreviousData } from "@tanstack/react-query";
import { columnLabelsMap, usersColumns } from "@/modules/users/TableColumnsDef.tsx";
import { PageHeader } from "@/components/PageHeader.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { Loader } from "@/components/Loader.tsx";
import { DataTable } from "@/components/table/DataTable.tsx";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { Export } from "@/modules/users/Export.tsx";
import { getVisibilityStateValue, setVisibilityStateValue } from "@/lib/table.ts";

export const Route = createFileRoute("/settings/users/")({
  component: RouteComponent,
});

const USERS_COLUMN_VISIBILITY_KEY = "@fin_merch_users_visible_columns";
export const USERS_LOCATION_KEY = "@fin_merch_users_location";

function RouteComponent() {
  const title = t`Users administration`;
  const { activeApp } = useAppConfig();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    getVisibilityStateValue(USERS_COLUMN_VISIBILITY_KEY),
  );

  useEffect(() => {
    setVisibilityStateValue(USERS_COLUMN_VISIBILITY_KEY, columnVisibility);
  }, [columnVisibility]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const { data, isPending, isError, error } = useUsersQuery(
    { pageNumber: 0, pageSize: 50 },
    { merchantId: activeApp },
    { enabled: !!activeApp, placeholderData: keepPreviousData },
  );

  const columns = useMemo(usersColumns, []);

  const table = useReactTable({
    columns,
    data: data?.users ?? [],
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
  });

  const labels = useMemo(() => {
    return columnLabelsMap();
  }, []);

  return (
    <PageWrapper>
      <PageHeader breadcrumbs={[{ href: "/", title }]} />
      <div className="flex flex-col grow p-5">
        {isError && (
          <FetchingError
            message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
            error={error}
          />
        )}
        {isPending && <Loader message={t`Loading users...`} />}
        {!isPending && !isError && (
          <DataTable
            table={table}
            labels={labels}
            showPagination={false}
            isUsersData={true}
            detailRoute={"/settings/users/$id"}
            detailIdKey={"userId"}
            exportComponent={<Export table={table} />}
            locationKey={USERS_LOCATION_KEY}
          />
        )}
      </div>
    </PageWrapper>
  );
}
